import { any, arrayOf, func } from 'prop-types';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import RHFAutocomplete from '#component/form/rhf/rhf-autocomplete';
import AutocompleteServiceFieldDescription from './autocomplete-services-field-description';

const getOptionLabel = option => (typeof option === 'string' ? option : option.description ?? '');

/**
 * Renders RHFAutocomplete bound to react-hook-form.
 * Utilize custom hooks to fetch options.
 * @returns {React.FC}
 */
const RHFAutocompleteServiceField = ({
  name,
  onOptionChange,
  useOptionsService,
  useOptionsServiceArgs = [],
  useOptionServiceProvider,
  GeolocationButton,
  description,
  loading,
  change,
  ...restProps
}) => {
  const { options, status, ...serviceMethods } =
  useOptionsService(...(useOptionsServiceArgs ?? []), useOptionServiceProvider);
  const { setValue } = useFormContext() || {};

  const handleInputChange = async (event, value) => {
    if (event && value?.length > 1 &&
      // prevent autocomplete request on selected data
      event.type !== 'click') {
      serviceMethods.fetchOptions(value);

      if (typeof change === 'function') {
        change(name, value);
      } else if (name && typeof setValue === 'function') {
        setValue(name, value, { shouldDirty: true });
      }
    } else {
      serviceMethods.fetchOptions(null);
    }
  };

  const handleChange = async (event, value) => {
    if (typeof value !== 'string' && value !== null) {
      const optionDetails = await value?.fetchDetails() ?? {};
      onOptionChange(optionDetails);
    }
    // removes loading on delete
    if (value === null) {
      serviceMethods.dispatchClearOptions?.();
    }
  };

  if (GeolocationButton) {
    if (restProps.open !== false) {
      restProps.open = Boolean(options.length) || status === 'loading';
    }

    restProps.inputProps = restProps.inputProps || {};
    restProps.inputProps.InputProps = {
      ...restProps.inputProps.InputProps,
      startAdornment: <GeolocationButton
        serviceMethods={serviceMethods}
        disabled={restProps?.disabled}
      />
    };
  }

  if (description) {
    restProps.inputProps = restProps.inputProps || {};
    restProps.inputProps.InputProps = {
      ...restProps.inputProps.InputProps,
      endAdornment: <AutocompleteServiceFieldDescription description={description} />
    };
    restProps.className = 'withDescription';
  }

  return (
    <RHFAutocomplete
      name={name}
      fullWidth
      onInputChange={handleInputChange}
      onChange={handleChange}
      options={options}
      getOptionLabel={getOptionLabel}
      serviceMethods={serviceMethods}
      freeSolo
      {...restProps}
      loading={!loading && status === 'loading'}
    />
  );
};

RHFAutocompleteServiceField.propTypes = {
  useOptionsService: func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  useOptionsServiceArgs: arrayOf(any),
  onOptionChange: func.isRequired
};

RHFAutocompleteServiceField.defaultProps = {
  useOptionsServiceArgs: []
};

export default RHFAutocompleteServiceField;
